import * as t from "io-ts";
import {
  IElement,
  SelectorTypes,
  TypeFactory,
  UntransformedConfig,
  arrayChild,
  customExpression,
} from "core/types";
import { Type, types } from "core/runtime-typing";
import { IAsyncActionState } from "core/utils/types";
import { selectors } from "core/editor/reduxModule";
import { buildNullableObjectViewType } from "core/runtime-typing/utils";

export const FormMultiReferenceConfig = t.type({
  viewName: t.string,
  identifierFieldName: t.string,
  referencingFieldName: t.string,
});

export type FormMultiReferenceConfig = t.TypeOf<
  typeof FormMultiReferenceConfig
>;

export enum Types {
  stay = "stay",
  navigateToPage = "navigate_to_page",
  goBack = "go_back",
}

export enum FormTypes {
  create = "create",
  edit = "edit",
  detail = "detail",
}

const LinkToPage = t.type({
  type: t.literal(Types.navigateToPage),
  pageId: customExpression(t.string),
  params: t.record(t.string, customExpression(t.unknown)),
});

const LinkToStay = t.type({
  type: t.literal(Types.stay),
});

const LinkToGoBack = t.type({
  type: t.literal(Types.goBack),
});

export type LinkToPage = t.TypeOf<typeof LinkToPage>;
export type LinkToStay = t.TypeOf<typeof LinkToStay>;
export type LinkToGoBack = t.TypeOf<typeof LinkToGoBack>;

const linkTo = t.union([LinkToPage, LinkToStay, LinkToGoBack]);

/**
 * if no value is specified, go back
 * if no "type" property exists on the object, assume it's a `LinkToPage`
 */
export function legacy2LinkTo(u: unknown) {
  if (u === undefined) {
    return { type: Types.goBack } as const;
  }
  if (typeof u === "object" && u !== null) {
    if ("type" in u) {
      return u;
    } else {
      return { ...u, type: Types.navigateToPage };
    }
  }
  return u;
}

/**
 * apply the legacy rules to stay backwards compatible
 */
const LegacyLinkTo = new t.Type(
  "LegacyDefaultFormLinkTo",
  // TODO the actual return type is the legacy linkTo, not the new one
  (u): u is t.TypeOf<typeof linkTo> => {
    return linkTo.is(legacy2LinkTo(u));
  },
  (u, c) => {
    return linkTo.validate(legacy2LinkTo(u), c);
  },
  linkTo.encode,
);

export const FormConfig = t.intersection([
  t.type({
    dataSource: t.intersection([
      t.type({
        viewName: t.string,
      }),
      t.partial({
        identifierName: t.string,
        identifierValue: customExpression(t.union([t.number, t.string])),
        multiReference: t.record(t.string, FormMultiReferenceConfig),
        stateFieldName: t.string,
      }),
    ]),
    type: t.union([
      t.literal(FormTypes.edit),
      t.literal(FormTypes.create),
      t.literal(FormTypes.detail),
    ]),
    linkTo: LegacyLinkTo,
  }),
  t.partial({
    jsonSchema: t.object,
    validation: customExpression(t.boolean),
    defaultData: t.record(t.string, customExpression(t.unknown)),
    hideSaveAndStayButton: t.boolean,
    disableSpacing: t.boolean,
  }),
]);

export const FormChildren = t.type({
  content: arrayChild("*", {
    positioned: true,
  }),
});

const dataSelector: TypeFactory<FormConfig> = ({ config, state }) => {
  let type: Type;
  const { multiReference, viewName } = config.dataSource;
  const viewList = selectors.viewList(state);
  if (!viewList) {
    // view list still loading, do not show any typing errors
    type = types.any();
  } else if (!viewName) {
    type = types.optional(types.null(), "no source view set");
  } else {
    const view = viewList.find((v) => v.name === viewName);
    if (!view) {
      throw new Error(`Invalid view ${viewName}`);
    }
    const referenceTypes: Record<PropertyKey, Type> = {};
    if (multiReference) {
      for (const referenceField in multiReference) {
        const referenceViewName = multiReference[referenceField].viewName;
        const referenceView = viewList.find(
          (v) => v.name === referenceViewName,
        );
        if (!referenceView) {
          throw new Error(`Invalid view ${referenceViewName}`);
        }
        referenceTypes[referenceField] = types.array(
          buildNullableObjectViewType(referenceView, true),
        );
      }
    }
    type = buildNullableObjectViewType(view, true, undefined, referenceTypes);
    type = types.nullable(type);
  }
  return type;
};

export const formSelectors: SelectorTypes<FormConfig> = {
  loadState: IAsyncActionState,
  saveState: IAsyncActionState,
  data: dataSelector,
  errors: types.anyRecord(),
  originalData: dataSelector,
  touched: types.record(
    types.union([types.string(), types.number()]),
    types.boolean(),
  ),
  hasChanges: types.optional(types.boolean()),
  isValid: types.optional(types.boolean()),
  identifier: types.optional(types.union([types.string(), types.number()])),
};

export type FormConfig = t.TypeOf<typeof FormConfig>;

export type UntransformedFormConfig = UntransformedConfig<FormConfig>;

export type FormChildren = t.TypeOf<typeof FormChildren>;

export type Form = IElement<FormConfig, FormChildren>;
